// Client-side fog of war. Mirrors the server's vision rule — every hex within
// VISION_R of own territory or own units is visible — purely for RENDERING:
// the server already refuses to send anything outside vision, so this module
// only decides where the shroud is drawn (dim = explored terrain out of sight,
// dark = the void of never-sent chunks) and lets the UI ask "can I see (q,r)?".

import { CHUNK, DIRS, SQRT3, chunkKey } from '../../shared/hex'
import { VISION_R } from '../../shared/constants'
import { HEX_SIZE, type GameState } from '../state'

// Same packed-hex scheme as the server's vision module.
const OFF = 1 << 21
const SPAN = 1 << 22

function packHex(q: number, r: number): number {
  return (q + OFF) * SPAN + (r + OFF)
}

/** Rings of soft fade past the vision edge (visible → memory gradient). */
export const FOG_FEATHER = 3

export interface FogState {
  /** Every currently visible hex, packed. */
  hexes: Set<number>
  /** Hexes just past the vision edge: packed → ring 1..FOG_FEATHER (fade band). */
  soft: Map<number, number>
  /** Every hex this faction has ever laid eyes on (session + local memory). */
  seen: Set<number>
  /** Chunk keys containing at least one visible hex. */
  chunks: Set<string>
  /** Chunk keys (and their neighbours) whose seen-memory changed this recompute —
   *  terrain must re-bake them so remembered edges knit across chunk seams. */
  memDirty: string[]
  /** Bumps when the visible set actually changes; consumers key caches off it. */
  version: number
}

let cache: FogState = { hexes: new Set(), soft: new Map(), seen: new Set(), chunks: new Set(), memDirty: [], version: 0 }
let lastKey = ''
let lastHash = -1 // order-independent hash of the visible set: skip version bumps when sight is unchanged

// ---- Ever-seen memory --------------------------------------------------------------
// The server streams terrain a whole 16×16 chunk at a time, but a player has only
// actually SEEN the hexes their radius-10 bubble touched. We remember those per hex
// so never-seen ground renders as void and exploration reveals hex by hex.
// Persisted in localStorage as one 256-bit bitmap per chunk, per faction.

let seenSet = new Set<number>()
let seenFor = '' // faction the set belongs to
let knownChunks = new Set<string>() // chunk keys already classified this session
let seenDirty = false
let seenSavedAt = 0

function seenStoreKey(me: string): string {
  return `serfland_fogseen_${me}`
}

function loadSeen(me: string): void {
  seenFor = me
  seenSet = new Set()
  knownChunks = new Set()
  try {
    const raw = localStorage.getItem(seenStoreKey(me))
    if (!raw) return
    const obj = JSON.parse(raw) as Record<string, string>
    for (const key in obj) {
      const [cxs, cys] = key.split(',')
      const cx = Number(cxs), cy = Number(cys)
      const bytes = atob(obj[key])
      for (let i = 0; i < CHUNK * CHUNK; i++) {
        if (bytes.charCodeAt(i >> 3) & (1 << (i & 7))) {
          seenSet.add(packHex(cx * CHUNK + (i % CHUNK), cy * CHUNK + Math.floor(i / CHUNK)))
        }
      }
    }
  } catch { /* corrupt/blocked storage: start fresh */ }
}

function saveSeen(st: GameState): void {
  if (!st.me) return
  try {
    const perChunk = new Map<string, Uint8Array>()
    for (const p of seenSet) {
      const q = Math.floor(p / SPAN) - OFF
      const r = (p % SPAN) - OFF
      const key = chunkKey(Math.floor(q / CHUNK), Math.floor(r / CHUNK))
      let bits = perChunk.get(key)
      if (!bits) { bits = new Uint8Array((CHUNK * CHUNK) >> 3); perChunk.set(key, bits) }
      const lq = ((q % CHUNK) + CHUNK) % CHUNK
      const lr = ((r % CHUNK) + CHUNK) % CHUNK
      const i = lr * CHUNK + lq
      bits[i >> 3] |= 1 << (i & 7)
    }
    const obj: Record<string, string> = {}
    for (const [key, bits] of perChunk) obj[key] = btoa(String.fromCharCode(...bits))
    localStorage.setItem(seenStoreKey(st.me), JSON.stringify(obj))
  } catch { /* storage full/blocked: memory stays session-only */ }
}

/** True if the faction has ever seen this hex (void otherwise). */
export function fogSeen(f: FogState | null, q: number, r: number): boolean {
  if (!f) return true
  return f.seen.has(packHex(q, r))
}

// Hexes at exactly distance 2 (for the second erosion ring).
const RING2: { q: number; r: number }[] = (() => {
  const seen = new Set<string>()
  const out: { q: number; r: number }[] = []
  for (const a of DIRS) for (const b of DIRS) {
    const q = a.q + b.q, r = a.r + b.r
    if (q === 0 && r === 0) continue
    if (DIRS.some((d) => d.q === q && d.r === r)) continue
    const k = q + ',' + r
    if (!seen.has(k)) { seen.add(k); out.push({ q, r }) }
  }
  return out
})()

/** Deterministic per-hex 0..1 (same spirit as the terrain jitter hash). */
function hash01(q: number, r: number): number {
  return (((Math.imul(q, 0x9e3779b1) ^ Math.imul(r, 0x85ebca77)) >>> 0) % 1024) / 1024
}

/** Should this hex render as ground at all? Like fogSeen, but deep-memory hexes
 *  bordering the unknown get a ragged, eroded rim (deterministic noise) so
 *  remembered map edges read as organic coastlines instead of ruler-straight
 *  chunk seams. Purely cosmetic: an eroded hex is still seen, and re-appears
 *  whenever it comes back in (or near) sight. */
export function fogGround(f: FogState | null, q: number, r: number): boolean {
  if (!f) return true
  const p = packHex(q, r)
  if (!f.seen.has(p)) return false
  if (f.hexes.has(p) || f.soft.has(p)) return true // in or near sight: never erode
  for (const d of DIRS) {
    if (!f.seen.has(packHex(q + d.q, r + d.r))) return hash01(q, r) >= 0.55
  }
  for (const d of RING2) {
    if (!f.seen.has(packHex(q + d.q, r + d.r))) return hash01(q, r) >= 0.22
  }
  return true
}

/** Per-hex light factor: 1 in vision, easing down through the feather band to
 *  FOG_DIM_FLOOR for remembered-but-unseen ground. (Render-side helper.) */
export function fogFade(f: FogState | null, q: number, r: number, floor: number): number {
  if (!f) return 1
  const p = packHex(q, r)
  if (f.hexes.has(p)) return 1
  const ring = f.soft.get(p)
  if (ring !== undefined) return 1 - ((1 - floor) * ring) / (FOG_FEATHER + 1)
  return floor
}

/** Current fog, recomputed when the tick advances or the world composition
 *  changes (unit moves land with the tick, so 1 Hz tracks the server). */
export function fogOf(st: GameState): FogState | null {
  if (st.observer) return null // spectators see the whole world
  const key = `${st.tick}/${st.chunks.size}/${st.units.size}/${st.me ?? ''}`
  if (key === lastKey) return cache
  lastKey = key
  if (st.me && seenFor !== st.me) loadSeen(st.me)

  const hexes = new Set<number>()
  let hash = 0
  let frontier: { q: number; r: number }[] = []
  const seed = (q: number, r: number): void => {
    const p = packHex(q, r)
    if (!hexes.has(p)) {
      hexes.add(p)
      hash = (hash ^ p) >>> 0
      frontier.push({ q, r })
    }
  }
  if (st.me) {
    // Allies share vision (mirrors the server's union rule): seed from allied
    // territory and units too — both stream to us because they're in our vision.
    const friend = new Set<string>([st.me, ...(st.factions.get(st.me)?.allies ?? [])])
    for (const ck of st.chunks.values()) {
      for (const [idx, owner] of ck.owners) {
        if (!friend.has(owner)) continue
        seed(ck.cx * CHUNK + (idx % CHUNK), ck.cy * CHUNK + Math.floor(idx / CHUNK))
      }
    }
    for (const u of st.units.values()) {
      if (friend.has(u.owner)) seed(u.q, u.r)
    }
  }
  // BFS out to VISION_R (visible), then FOG_FEATHER more rings (the fade band).
  const soft = new Map<number, number>()
  for (let d = 0; d < VISION_R + FOG_FEATHER; d++) {
    const next: { q: number; r: number }[] = []
    for (const h of frontier) {
      for (const dir of DIRS) {
        const q = h.q + dir.q
        const r = h.r + dir.r
        const p = packHex(q, r)
        if (!hexes.has(p)) {
          hexes.add(p)
          next.push({ q, r })
          if (d < VISION_R) hash = (hash ^ p) >>> 0
          else soft.set(p, d - VISION_R + 1)
        }
      }
    }
    frontier = next
  }
  // The feather rings were added to `hexes` for BFS bookkeeping only — they are
  // NOT visible. Pull them back out.
  for (const p of soft.keys()) hexes.delete(p)

  // Grow the ever-seen memory with what's in sight right now.
  const seenBefore = seenSet.size
  for (const p of hexes) seenSet.add(p)
  // Chunks the server stamps `mem` were explored in an earlier session: count
  // them fully seen — even with no current sight at all (a ruined realm must
  // still show its remembered map). Fresh chunks (mem absent) reveal only what
  // the bubble actually sweeps, so they wait for sight context; without a stamp
  // at all (older server), fall back to "no vision contact = old memory".
  const memDirty: string[] = []
  if (st.me) for (const [key, ck] of st.chunks) {
    if (knownChunks.has(key)) continue
    if (ck.mem !== true && hexes.size === 0) continue
    knownChunks.add(key)
    let allSeen = ck.mem === true
    if (ck.mem === undefined) {
      let touched = false
      let stored = false
      for (let i = 0; i < CHUNK * CHUNK && !touched && !stored; i++) {
        const p = packHex(ck.cx * CHUNK + (i % CHUNK), ck.cy * CHUNK + Math.floor(i / CHUNK))
        if (hexes.has(p)) touched = true
        else if (seenSet.has(p)) stored = true
      }
      allSeen = !touched && !stored
    }
    if (allSeen) {
      for (let i = 0; i < CHUNK * CHUNK; i++) {
        seenSet.add(packHex(ck.cx * CHUNK + (i % CHUNK), ck.cy * CHUNK + Math.floor(i / CHUNK)))
      }
      // the chunk itself re-bakes via its data version; its neighbours must
      // re-bake too so eroded rims and edge skirts knit across the seam
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        memDirty.push(chunkKey(ck.cx + dx, ck.cy + dy))
      }
    }
  }
  const seenGrew = seenSet.size !== seenBefore
  if (seenGrew) seenDirty = true
  if (seenDirty && performance.now() - seenSavedAt > 15_000) {
    seenSavedAt = performance.now()
    seenDirty = false
    saveSeen(st)
  }

  const chunkPacked = new Set<number>()
  const addChunkOf = (p: number): void => {
    const q = Math.floor(p / SPAN) - OFF
    const r = (p % SPAN) - OFF
    chunkPacked.add(packHex(Math.floor(q / CHUNK), Math.floor(r / CHUNK)))
  }
  for (const p of hexes) addChunkOf(p)
  for (const p of soft.keys()) addChunkOf(p) // fade band re-bakes ride along
  const chunks = new Set<string>()
  for (const p of chunkPacked) {
    chunks.add(chunkKey(Math.floor(p / SPAN) - OFF, (p % SPAN) - OFF))
  }
  // Same sight and memory as last recompute (idle town, nothing moved): keep the
  // version so consumers don't re-bake terrain for nothing.
  const sightChanged = hash !== lastHash || hexes.size !== cache.hexes.size
  const version = sightChanged || seenGrew ? cache.version + 1 : cache.version
  lastHash = hash
  cache = { hexes, soft, seen: seenSet, chunks, memDirty, version }
  return cache
}

export function fogVisible(f: FogState | null, q: number, r: number): boolean {
  if (!f) return true
  return f.hexes.has(packHex(q, r))
}

// ---- Shroud rendering -------------------------------------------------------------
// A world-space offscreen mask covering the viewport plus a margin, re-baked only
// when the fog changes or the camera leaves the margin. Fogged hexes are painted
// as SOLID overlapping boxes at low resolution, then composited once with alpha —
// solid-then-alpha keeps overlaps uniform, and the upscale feathers the edges.

const BAKE_SCALE = 0.22 // offscreen px per world px: one hex ≈ 9 px, soft when upscaled
const MARGIN = HEX_SIZE * 24 // world-px margin so panning doesn't re-bake every frame
const SHROUD_ALPHA = 0.52
const VOID_ALPHA = 0.94 // never-seen ground: almost solid dark
const SHROUD_COLOR = '#0a1018'

let mask: HTMLCanvasElement | null = null
let voidMask: HTMLCanvasElement | null = null
let maskX = 0
let maskY = 0
let maskW = 0
let maskH = 0
let maskVersion = -1

function bakeMask(f: FogState, minX: number, minY: number, maxX: number, maxY: number): void {
  maskX = minX - MARGIN
  maskY = minY - MARGIN
  maskW = maxX - minX + MARGIN * 2
  maskH = maxY - minY + MARGIN * 2
  const w = Math.max(1, Math.ceil(maskW * BAKE_SCALE))
  const h = Math.max(1, Math.ceil(maskH * BAKE_SCALE))
  if (!mask) mask = document.createElement('canvas')
  if (!voidMask) voidMask = document.createElement('canvas')
  for (const cv of [mask, voidMask]) {
    if (cv.width !== w) cv.width = w
    if (cv.height !== h) cv.height = h
  }
  const mctx = mask.getContext('2d')
  const vctx = voidMask.getContext('2d')
  if (!mctx || !vctx) return
  for (const c of [mctx, vctx]) {
    c.setTransform(1, 0, 0, 1, 0, 0)
    c.clearRect(0, 0, w, h)
    c.fillStyle = SHROUD_COLOR
  }

  // Iterate every hex whose centre falls inside the mask rect; fogged hexes get
  // a solid box (slightly oversized so neighbours weld into a continuous veil).
  // Remembered ground goes in the dim mask; never-seen ground also goes in the
  // (near-solid) void mask. Feather rings paint at partial alpha so the shroud
  // eases in over a few hexes instead of a hard line.
  const S = HEX_SIZE
  const colW = SQRT3 * S
  const rowH = 1.5 * S
  const r0 = Math.floor(maskY / rowH) - 1
  const r1 = Math.ceil((maskY + maskH) / rowH) + 1
  const box = colW * BAKE_SCALE * 1.25
  for (let r = r0; r <= r1; r++) {
    // world x of hex (q,r) is colW * (q + r/2): solve q range for the rect
    const q0 = Math.floor(maskX / colW - r / 2) - 1
    const q1 = Math.ceil((maskX + maskW) / colW - r / 2) + 1
    const py = (r * rowH - maskY) * BAKE_SCALE
    for (let q = q0; q <= q1; q++) {
      const p = packHex(q, r)
      if (f.hexes.has(p)) continue
      const px = (colW * (q + r / 2) - maskX) * BAKE_SCALE
      const ring = f.soft.get(p)
      mctx.globalAlpha = ring !== undefined ? ring / (FOG_FEATHER + 1) : 1
      mctx.fillRect(px - box / 2, py - box / 2, box, box)
      if (!fogGround(f, q, r)) vctx.fillRect(px - box / 2, py - box / 2, box, box)
    }
  }
  mctx.globalAlpha = 1
}

/**
 * Draw the shroud over everything already painted in WORLD space: fogged loaded
 * terrain dims to "explored memory", the void stays dark, visible land is
 * untouched. Call with the ctx still in the world transform.
 */
export function drawFog(
  ctx: CanvasRenderingContext2D,
  st: GameState,
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
): void {
  const f = fogOf(st)
  if (!f) return // spectator: nothing shrouded
  const stale =
    maskVersion !== f.version ||
    !mask ||
    minX < maskX ||
    minY < maskY ||
    maxX > maskX + maskW ||
    maxY > maskY + maskH
  if (stale) {
    bakeMask(f, minX, minY, maxX, maxY)
    maskVersion = f.version
  }
  if (!mask) return
  ctx.imageSmoothingEnabled = true
  ctx.globalAlpha = SHROUD_ALPHA
  ctx.drawImage(mask, maskX, maskY, maskW, maskH)
  if (voidMask) {
    // never-seen ground goes nearly solid on top of the dim veil
    ctx.globalAlpha = VOID_ALPHA
    ctx.drawImage(voidMask, maskX, maskY, maskW, maskH)
  }
  ctx.globalAlpha = 1
}

/** True when a minimap tile should be dimmed (out of current vision). */
export function minimapFog(f: FogState | null, q: number, r: number): boolean {
  if (!f) return false
  return !f.hexes.has(packHex(q, r))
}

// ---- Camera bounds ---------------------------------------------------------------
// Scrolling is confined to the discovered map (loaded chunks — explored terrain,
// dimmed or lit) plus a margin of twice the fog radius. There is nothing to see
// in the deep void, and the clamp keeps players from getting lost in it.

const CAM_MARGIN_X = 2 * VISION_R * HEX_SIZE * SQRT3 // 20 hex-widths
const CAM_MARGIN_Y = 2 * VISION_R * HEX_SIZE * 1.5 // 20 hex-rows

let camBox: { minX: number; minY: number; maxX: number; maxY: number } | null = null
let camBoxChunks = -1

/** Forget the discovered-map bounds. MUST be called when the world data is
 *  replaced wholesale (a welcome after restart/respawn): the cached box spans
 *  the OLD realm, and the very next frame's clamp would yank the freshly
 *  home-centred camera back toward it — leaving the player staring at fog
 *  between their old and new villages. */
export function resetCamBounds(): void {
  camBox = null
  camBoxChunks = -1
}

/** Clamp the camera into the discovered world + margin. Call once per frame —
 *  it catches every pan source (keys, drag, minimap jump, zoom-at-cursor). */
export function clampCamera(st: GameState): void {
  if (st.observer) return // spectators roam freely
  if (st.chunks.size === 0) return
  if (st.chunks.size !== camBoxChunks) {
    camBoxChunks = st.chunks.size
    const S = HEX_SIZE
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const ck of st.chunks.values()) {
      const q0 = ck.cx * CHUNK
      const q1 = q0 + CHUNK
      const r0 = ck.cy * CHUNK
      const r1 = r0 + CHUNK
      // world px: x = S√3·(q + r/2), y = 1.5S·r — chunk extremes at its corners
      minX = Math.min(minX, S * SQRT3 * (q0 + r0 / 2))
      maxX = Math.max(maxX, S * SQRT3 * (q1 + r1 / 2))
      minY = Math.min(minY, S * 1.5 * r0)
      maxY = Math.max(maxY, S * 1.5 * r1)
    }
    camBox = { minX, minY, maxX, maxY }
  }
  if (!camBox) return
  st.cam.x = Math.min(camBox.maxX + CAM_MARGIN_X, Math.max(camBox.minX - CAM_MARGIN_X, st.cam.x))
  st.cam.y = Math.min(camBox.maxY + CAM_MARGIN_Y, Math.max(camBox.minY - CAM_MARGIN_Y, st.cam.y))
}
